import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/constants';

const EmployeesModule = ({ onBack }) => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showActiveOnly, setShowActiveOnly] = useState(true);

  React.useEffect(() => {
    fetchEmployees();
  }, [showActiveOnly]);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const endpoint = showActiveOnly ? '/employees/active' : '/employees';
      const response = await axios.get(`${API}${endpoint}`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEditEmployee = (employee) => {
    setEditingEmployee(employee);
    setShowEditModal(true);
  };

  const handleDeleteEmployee = async (employeeId, employeeName) => {
    if (window.confirm(`Are you sure you want to delete ${employeeName}?\n\nNote: If they have active job assignments, deletion will be prevented.`)) {
      try {
        const response = await axios.delete(`${API}/employees/${employeeId}`);
        alert(response.data.message || 'Employee deleted successfully!');
        fetchEmployees();
      } catch (error) {
        console.error('Error deleting employee:', error);
        
        if (error.response?.status === 409) {
          // Employee has active assignments
          const message = error.response.data.detail;
          const forceDelete = window.confirm(
            `${message}\n\nWould you like to FORCE DELETE this employee and remove them from all assignments?\n\n⚠️ Warning: This will remove them from active jobs and may affect scheduling.`
          );
          
          if (forceDelete) {
            try {
              const forceResponse = await axios.delete(`${API}/employees/${employeeId}/force`);
              alert(forceResponse.data.message || 'Employee force deleted successfully!');
              fetchEmployees();
            } catch (forceError) {
              console.error('Error force deleting employee:', forceError);
              alert(`Error force deleting employee: ${forceError.response?.data?.detail || forceError.message}`);
            }
          }
        } else {
          alert(`Error deleting employee: ${error.response?.data?.detail || error.message}`);
        }
      }
    }
  };

  const handleDeactivateEmployee = async (employeeId, employeeName) => {
    if (window.confirm(`Are you sure you want to deactivate ${employeeName}?`)) {
      try {
        await axios.put(`${API}/employees/${employeeId}/deactivate`);
        alert('Employee deactivated successfully!');
        fetchEmployees();
      } catch (error) {
        console.error('Error deactivating employee:', error);
        alert('Error deactivating employee. Please try again.');
      }
    }
  };

  const handleActivateEmployee = async (employeeId, employeeName) => {
    try {
      await axios.put(`${API}/employees/${employeeId}/activate`);
      alert('Employee activated successfully!');
      fetchEmployees();
    } catch (error) {
      console.error('Error activating employee:', error);
      alert('Error activating employee. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800">
      <div className="container mx-auto px-4 py-8">
        
        {/* Header - Mobile Responsive */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 sm:mb-8 gap-4">
          <button onClick={onBack} className="text-blue-600 hover:text-blue-700 text-xl sm:text-2xl self-start">← Back</button>
          <h1 className="text-2xl sm:text-4xl font-bold text-gray-800 text-center sm:text-left">EMPLOYEE MANAGEMENT</h1>
          <button 
            onClick={() => setShowAddModal(true)}
            className="bg-blue-600 hover:bg-blue-700 px-4 py-3 sm:px-6 rounded-xl font-bold text-white shadow-lg text-sm sm:text-base"
          >
            + ADD EMPLOYEE
          </button>
        </div>

        {/* Controls - Mobile Responsive */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowActiveOnly(!showActiveOnly)}
              className={`px-3 py-2 sm:px-4 rounded-lg font-semibold text-sm sm:text-base ${
                showActiveOnly 
                  ? 'bg-green-600 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {showActiveOnly ? '✅ Active Only' : '👥 All Employees'}
            </button>
          </div>
          <div className="text-blue-600 font-semibold text-sm sm:text-base">
            {employees.length} employees found
          </div>
        </div>

        {loading && (
          <div className="text-center py-12">
            <div className="text-2xl text-blue-600">Loading employees...</div>
          </div>
        )}

        {!loading && employees.length === 0 && (
          <div className="text-center py-12">
            <div className="text-xl text-gray-500 mb-4">No employees found</div>
            <button 
              onClick={() => setShowAddModal(true)}
              className="bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-xl font-bold text-white"
            >
              Add First Employee
            </button>
          </div>
        )}

        {!loading && employees.length > 0 && (
          <div className="grid gap-4">
            {employees.map((employee) => (
              <div key={employee.id} className="bg-white rounded-xl p-4 sm:p-6 shadow-lg border border-gray-200">
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start">
                  <div className="flex-1 mb-4 lg:mb-0">
                    {/* Employee Header - Mobile Responsive */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-3">
                      <h3 className="text-lg sm:text-xl font-bold text-gray-800">
                        {employee.first_name} {employee.last_name}
                      </h3>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="px-2 py-1 sm:px-3 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                          {employee.position}
                        </span>
                        {employee.position_title && (
                          <span className="px-2 py-1 sm:px-3 bg-purple-100 text-purple-800 rounded-full text-xs font-semibold">
                            {employee.position_title}
                          </span>
                        )}
                        <span className="px-2 py-1 sm:px-3 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
                          {employee.department}
                        </span>
                        {!employee.active && (
                          <span className="px-2 py-1 sm:px-3 bg-red-100 text-red-800 rounded-full text-xs font-semibold">
                            INACTIVE
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Employee Information - Mobile Responsive Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                      <div className="space-y-2">
                        {employee.email && (
                          <p className="text-gray-600 text-sm sm:text-base break-all">
                            <span className="font-semibold">📧 Email:</span> {employee.email}
                          </p>
                        )}
                        {employee.phone && (
                          <p className="text-gray-600 text-sm sm:text-base">
                            <span className="font-semibold">📞 Phone:</span> {employee.phone}
                          </p>
                        )}
                        {employee.address && (
                          <p className="text-gray-600 text-sm sm:text-base break-words">
                            <span className="font-semibold">📍 Address:</span> {employee.address}
                          </p>
                        )}
                        {employee.hire_date && (
                          <p className="text-gray-600 text-sm sm:text-base">
                            <span className="font-semibold">📅 Hire Date:</span> {employee.hire_date}
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        {employee.emergency_contact_name && (
                          <p className="text-gray-600 text-sm sm:text-base break-words">
                            <span className="font-semibold">🚨 Emergency Contact:</span> {employee.emergency_contact_name}
                          </p>
                        )}
                        {employee.emergency_contact_phone && (
                          <p className="text-gray-600 text-sm sm:text-base">
                            <span className="font-semibold">🚨 Emergency Phone:</span> {employee.emergency_contact_phone}
                          </p>
                        )}
                        {employee.notes && (
                          <p className="text-gray-600 text-sm sm:text-base break-words">
                            <span className="font-semibold">📝 Notes:</span> {employee.notes}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Action Buttons - Mobile Responsive */}
                  <div className="flex flex-row lg:flex-col gap-2 lg:ml-4">
                    <button
                      onClick={() => handleEditEmployee(employee)}
                      className="bg-amber-600 hover:bg-amber-700 px-3 py-2 sm:px-4 rounded-lg text-white font-bold text-xs sm:text-sm shadow-sm flex-1 lg:flex-none"
                    >
                      Edit
                    </button>
                    {employee.active ? (
                      <button
                        onClick={() => handleDeactivateEmployee(employee.id, `${employee.first_name} ${employee.last_name}`)}
                        className="bg-orange-600 hover:bg-orange-700 px-3 py-2 sm:px-4 rounded-lg text-white font-bold text-xs sm:text-sm shadow-sm flex-1 lg:flex-none"
                      >
                        Deactivate
                      </button>
                    ) : (
                      <button
                        onClick={() => handleActivateEmployee(employee.id, `${employee.first_name} ${employee.last_name}`)}
                        className="bg-green-600 hover:bg-green-700 px-3 py-2 sm:px-4 rounded-lg text-white font-bold text-xs sm:text-sm shadow-sm flex-1 lg:flex-none"
                      >
                        Activate
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteEmployee(employee.id, `${employee.first_name} ${employee.last_name}`)}
                      className="bg-red-600 hover:bg-red-700 px-3 py-2 sm:px-4 rounded-lg text-white font-bold text-xs sm:text-sm shadow-sm flex-1 lg:flex-none"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Edit/Add Employee Modal */}
        {(showEditModal || showAddModal) && (
          <EmployeeEditModal
            employee={editingEmployee}
            onClose={() => {
              setShowEditModal(false);
              setShowAddModal(false);
              setEditingEmployee(null);
            }}
            onUpdate={() => fetchEmployees()}
          />
        )}
        </div>
      </div>
  );
};

// Employee Edit Modal Component
const EmployeeEditModal = ({ employee, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    first_name: employee?.first_name || '',
    last_name: employee?.last_name || '',
    position: employee?.position || '',
    department: employee?.department || '',
    email: employee?.email || '',
    phone: employee?.phone || '',
    address: employee?.address || '',
    hire_date: employee?.hire_date || '',
    emergency_contact_name: employee?.emergency_contact_name || '',
    emergency_contact_phone: employee?.emergency_contact_phone || '',
    notes: employee?.notes || '',
    active: employee?.active !== undefined ? employee.active : true
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (employee?.id) {
        // Update existing employee
        await axios.put(`${API}/employees/${employee.id}`, formData);
        alert('Employee updated successfully!');
      } else {
        // Create new employee
        await axios.post(`${API}/employees`, formData);
        alert('Employee created successfully!');
      }
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error saving employee:', error);
      alert('Error saving employee. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-[60] p-4 pt-12 sm:pt-4 overflow-y-auto p-4">
      <div className="bg-white rounded-xl p-4 sm:p-6 lg:p-8 max-w-4xl w-full shadow-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-gray-800">
          {employee?.id ? 'Edit Employee' : 'Add New Employee'}
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                First Name *
              </label>
              <input
                type="text"
                value={formData.first_name}
                onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                required
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Last Name *
              </label>
              <input
                type="text"
                value={formData.last_name}
                onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Position *
              </label>
              <input
                type="text"
                value={formData.position}
                onChange={(e) => setFormData({...formData, position: e.target.value})}
                className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                placeholder="e.g. Equipment Operator, Foreman, Admin"
                required
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Department *
              </label>
              <select
                value={formData.department}
                onChange={(e) => setFormData({...formData, department: e.target.value})}
                className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                required
              >
                <option value="">Select Department</option>
                <option value="Operations">Operations</option>
                <option value="Equipment">Equipment</option>
                <option value="Administration">Administration</option>
                <option value="Safety">Safety</option>
                <option value="Management">Management</option>
              </select>
            </div>
          </div>

          {/* Contact Information */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Email
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Phone
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
              />
            </div>
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Address
            </label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
            />
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Hire Date
            </label>
            <input
              type="date"
              value={formData.hire_date}
              onChange={(e) => setFormData({...formData, hire_date: e.target.value})}
              className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
            />
          </div>

          {/* Emergency Contact */}
          <div className="bg-red-50 p-3 sm:p-4 rounded-lg border border-red-200">
            <h3 className="text-red-700 font-bold mb-3 text-sm sm:text-base">🚨 Emergency Contact</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Emergency Contact Name
                </label>
                <input
                  type="text"
                  value={formData.emergency_contact_name}
                  onChange={(e) => setFormData({...formData, emergency_contact_name: e.target.value})}
                  className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Emergency Contact Phone
                </label>
                <input
                  type="tel"
                  value={formData.emergency_contact_phone}
                  onChange={(e) => setFormData({...formData, emergency_contact_phone: e.target.value})}
                  className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
              rows="3"
              placeholder="Additional notes about the employee..."
            />
          </div>

          {/* Active Status */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="active"
              checked={formData.active}
              onChange={(e) => setFormData({...formData, active: e.target.checked})}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="active" className="ml-2 text-sm font-medium text-gray-700">
              Active Employee
            </label>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <button 
              type="submit" 
              disabled={loading}
              className="w-full sm:flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 py-3 rounded-lg font-bold text-white shadow-sm text-sm sm:text-base"
            >
              {loading ? 'Saving...' : (employee?.id ? 'Update Employee' : 'Add Employee')}
            </button>
            <button 
              type="button" 
              onClick={onClose}
              className="w-full sm:flex-1 bg-gray-500 hover:bg-gray-600 py-3 rounded-lg font-bold text-white shadow-sm text-sm sm:text-base"
            >
              Cancel
            </button>
          </div>
        </form>
        </div>
      </div>
  );
};

// HR-Specific Employees Component with Additional Fields

const EmployeeContactsModule = ({ onBack }) => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hrPositions, setHrPositions] = useState([]);

  const positionLabel = (posId) => {
    if (!posId) return '';
    return hrPositions.find((p) => p.id === posId)?.title || posId;
  };

  React.useEffect(() => {
    fetchEmployees();
  }, []);

  React.useEffect(() => {
    const fetchPositions = async () => {
      try {
        const res = await axios.get(`${API}/hr/positions`);
        setHrPositions(res.data || []);
      } catch (e) {
        console.error('Error fetching HR positions (contacts):', e);
        setHrPositions([]);
      }
    };
    fetchPositions();
  }, []);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/employees/active`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredEmployees = employees.filter(employee => {
    const fullName = `${employee.first_name || ''} ${employee.last_name || ''}`.trim();
    return fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.position?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 sm:px-6 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <button 
                onClick={onBack}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors"
              >
                ← Back to Production
              </button>
              <h1 className="text-4xl font-bold text-gray-800">👷 Employee Contacts</h1>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 py-8">
        {/* Search Bar */}
        <div className="mb-8">
          <div className="max-w-md">
            <input
              type="text"
              placeholder="Search by name, email, phone, or position..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <p className="text-sm text-gray-600 mt-2">
            {filteredEmployees.length} of {employees.length} employees shown
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="text-xl text-gray-600">Loading contacts...</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredEmployees.map((employee) => (
              <div key={employee.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                <div className="text-center mb-4">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-2xl font-bold text-blue-600">
                      {employee.first_name?.charAt(0)?.toUpperCase() || '?'}
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-1">
                    {`${employee.first_name || ''} ${employee.last_name || ''}`.trim() || 'Unknown Name'}
                  </h3>
                  <p className="text-gray-600 font-medium">
                    {positionLabel(employee.position) || 'No Position Set'}
                  </p>
                </div>

                <div className="space-y-3">
                  {employee.email && (
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                        <span className="text-gray-600">✉️</span>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Email</div>
                        <a 
                          href={`mailto:${employee.email}`}
                          className="text-blue-600 hover:text-blue-700 font-medium"
                        >
                          {employee.email}
                        </a>
                      </div>
                    </div>
                  )}

                  {employee.phone && (
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                        <span className="text-gray-600">📞</span>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Phone</div>
                        <a 
                          href={`tel:${employee.phone}`}
                          className="text-blue-600 hover:text-blue-700 font-medium"
                        >
                          {employee.phone}
                        </a>
                      </div>
                    </div>
                  )}

                  {employee.emergency_contact && (
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                        <span className="text-red-600">🚨</span>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Emergency Contact</div>
                        <div className="text-gray-700 font-medium text-sm">
                          {employee.emergency_contact}
                        </div>
                      </div>
                    </div>
                  )}

                  {employee.hire_date && (
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                        <span className="text-gray-600">📅</span>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Hire Date</div>
                        <div className="text-gray-700 font-medium text-sm">
                          {new Date(employee.hire_date).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {filteredEmployees.length === 0 && !loading && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">👥</div>
            <h3 className="text-xl font-semibold mb-2">No Employees Found</h3>
            <p className="text-gray-600">
              {searchTerm ? 'Try adjusting your search terms.' : 'No active employees to display.'}
            </p>
          </div>
        )}
        </div>
      </div>
  );
};

// Fleet Management Component

// Pier Logs Module Component

export default EmployeeContactsModule;
